import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClinicMedical } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import '../../styles/waitingroom.css';
import {
  SchedulingProvider,
  WaitingRoomAppointment,
  KanbanColumn,
  AppointmentStatus
} from '../../types/scheduling';

// Draggable appointment card component
const AppointmentCard: React.FC<{ appointment: WaitingRoomAppointment; isDragging?: boolean }> = ({ appointment, isDragging = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: appointment.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="waitingroom-card bg-base-100 rounded-lg shadow p-4 mb-3 cursor-move hover:shadow-lg transition-shadow border border-base-300"
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-bold text-lg text-base-content">{appointment.patient_name}</h4>
        <span className="text-sm text-base-content opacity-60">{appointment.scheduled_time}</span>
      </div>
      
      <div className="space-y-1 text-sm">
        <p>
          <span className="font-semibold">Type:</span> {appointment.appointment_type}
        </p>
        {appointment.room_name && (
          <p>
            <span className="font-semibold">Room:</span> {appointment.room_name}
          </p>
        )}
        {appointment.check_in_time && (
          <p>
            <span className="font-semibold">Checked In:</span> {appointment.check_in_time}
          </p>
        )}
        {appointment.consult_start_time && (
          <p>
            <span className="font-semibold">Started:</span> {appointment.consult_start_time}
          </p>
        )}
        {appointment.consult_end_time && (
          <p>
            <span className="font-semibold">Completed:</span> {appointment.consult_end_time}
          </p>
        )}
      </div>
    </div>
  );
};

// Droppable column component
const DroppableColumn: React.FC<{
  column: KanbanColumn;
  children: React.ReactNode;
}> = ({ column, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  return (
    <div className="flex flex-col bg-base-200 rounded-lg waitingroom-column">
      {/* Column Header — sticky so it stays pinned at the top of the
          viewport while the single page-level scrollbar scrolls the
          (potentially much longer) card lists beneath it. */}
      <div className="p-4 border-b border-base-300 sticky top-0 z-10 bg-base-200 rounded-t-lg">
        <h3 className="font-bold text-lg text-base-content">
          {column.title}
          <span className="ml-2 text-sm font-normal opacity-60">
            ({column.appointments.length})
          </span>
        </h3>
      </div>

      {/* Column Content - Droppable Container. No overflow/height clamp
          here — the whole board scrolls together at the page level. */}
      <div
        ref={setNodeRef}
        data-status={column.id}
        className={`flex-1 p-4 ${isOver ? 'bg-blue-50' : ''}`}
      >
        {children}

        {column.appointments.length === 0 && (
          <div className="text-center text-base-content opacity-40 py-8">
            No appointments
          </div>
        )}
      </div>
    </div>
  );
};

const SELECTED_PROVIDER_STORAGE_KEY = 'waitingroom_selected_provider_id';

const WaitingRoom: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedProviderId, setSelectedProviderIdState] = useState<string | number | null>(null);

  // Wraps setSelectedProviderId so every change (manual pick or
  // auto-select) is remembered across a page refresh.
  const setSelectedProviderId = (id: string | number | null) => {
    setSelectedProviderIdState(id);
    if (id) {
      localStorage.setItem(SELECTED_PROVIDER_STORAGE_KEY, id.toString());
    } else {
      localStorage.removeItem(SELECTED_PROVIDER_STORAGE_KEY);
    }
  };
  const [providers, setProviders] = useState<SchedulingProvider[]>([]);
  const [appointments, setAppointments] = useState<WaitingRoomAppointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [showNoProvidersModal, setShowNoProvidersModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Load providers
  const loadProviders = async () => {
    if (!token) {
      setProviders([]);
      setSelectedProviderId(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await apiCall('/providers/providers/', { method: 'GET' }, token);
      const data = await response.json();
      const providersList = data.results || data;
      setProviders(providersList);

      if (!Array.isArray(providersList) || providersList.length === 0) {
        setSelectedProviderId(null);
        setShowNoProvidersModal(true);
        setLoading(false);
        return;
      }
      
      if (providersList.length > 0 && !selectedProviderId) {
        // Restore whatever was selected before the last page refresh, if
        // that provider still exists in the current list.
        const savedProviderId = localStorage.getItem(SELECTED_PROVIDER_STORAGE_KEY);
        if (savedProviderId) {
          const savedProvider = providersList.find(
            (p: SchedulingProvider) => p.id.toString() === savedProviderId
          );
          if (savedProvider) {
            setSelectedProviderId(savedProvider.id);
            return;
          }
        }

        // First, try to match logged-in user to a provider by name
        if (user && user.first_name && user.last_name) {
          const userProvider = providersList.find((p: SchedulingProvider) => 
            p.first_name?.toLowerCase() === user.first_name.toLowerCase() &&
            p.last_name?.toLowerCase() === user.last_name.toLowerCase()
          );
          if (userProvider) {
            console.log('Matched logged-in user to provider:', userProvider);
            setSelectedProviderId(userProvider.id);
            return;
          }
        }
        
        // Fallback: Auto-select first provider if user is not a provider
        setSelectedProviderId(providersList[0].id);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
      setProviders([]);
      setSelectedProviderId(null);
      setShowNoProvidersModal(true);
      setLoading(false);
    }
  };

  // Load appointments for the selected date and provider
  const loadAppointments = async () => {
    if (!token || !selectedProviderId) {
      setAppointments([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Format date for API call (YYYY-MM-DD)
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Fetch appointments for this provider and date
      const response = await apiCall(
        `/scheduling/appointments/waiting_room/?provider_id=${selectedProviderId}&date=${dateStr}`,
        { method: 'GET' },
        token
      );
      
      const data = await response.json();
      setAppointments(data.results || data);
      
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (token) {
      loadProviders();
    } else {
      setProviders([]);
      setAppointments([]);
      setSelectedProviderId(null);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && selectedProviderId) {
      loadAppointments();
    }
  }, [token, selectedProviderId, currentDate]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const appointmentId = active.id.toString();

    // Find the appointment
    const appointment = appointments.find(apt => apt.id.toString() === appointmentId);

    // Resolve target status: can be a column id or another appointment card id
    let newStatus: AppointmentStatus | null = null;
    const overId = over.id.toString();
    const matchingColumn = columns.find((col) => col.id === overId);

    if (matchingColumn) {
      newStatus = matchingColumn.id;
    } else {
      const overAppointment = appointments.find((apt) => apt.id.toString() === overId);
      if (overAppointment) {
        newStatus = overAppointment.status;
      }
    }

    if (!newStatus) {
      setActiveId(null);
      return;
    }
    
    // Only update if status actually changed
    if (appointment && appointment.status !== newStatus) {
      updateAppointmentStatus(appointmentId, newStatus);
    }

    setActiveId(null);
  };

  // Update appointment status via API
  const updateAppointmentStatus = async (appointmentId: string, newStatus: AppointmentStatus) => {
    if (!token) return;

    try {
      // Update local state immediately for responsive UI
      setAppointments(prev => prev.map(apt => 
        apt.id.toString() === appointmentId 
          ? { ...apt, status: newStatus }
          : apt
      ));

      // Make API call to update backend
      await apiCall(
        `/scheduling/appointments/${appointmentId}/update_status/`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        },
        token
      );

      console.log(`Updated appointment ${appointmentId} to status: ${newStatus}`);
      
    } catch (error) {
      console.error('Error updating appointment status:', error);
      // Reload appointments to revert optimistic update on error
      await loadAppointments();
    }
  };

  // Get appointments by status
  const getAppointmentsByStatus = (status: AppointmentStatus): WaitingRoomAppointment[] => {
    if (!Array.isArray(appointments)) {
      return [];
    }
    return appointments.filter(apt => apt.status === status);
  };

  // Helper function to clean provider names
  const cleanProviderName = (provider: SchedulingProvider): string => {
    const fullName = provider.full_name || `${provider.first_name} ${provider.last_name}`;
    return fullName.split(',')[0].trim();
  };

  // Date navigation helpers
  const formatDate = (date: Date): string => {
    const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-AU', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinal = (n: number): string => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${weekday}, ${getOrdinal(day)} ${month}, ${year}`;
  };

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Kanban columns configuration
  const columns: KanbanColumn[] = [
    {
      id: 'scheduled',
      title: 'Scheduled',
      appointments: getAppointmentsByStatus('scheduled')
    },
    {
      id: 'in_waiting_room',
      title: 'In Waiting Room',
      appointments: getAppointmentsByStatus('in_waiting_room')
    },
    {
      id: 'in_consult',
      title: 'In Consult',
      appointments: getAppointmentsByStatus('in_consult')
    },
    {
      id: 'to_be_billed',
      title: 'To be Billed',
      appointments: getAppointmentsByStatus('to_be_billed')
    },
    {
      id: 'completed',
      title: 'Completed',
      appointments: getAppointmentsByStatus('completed')
    }
  ];

  // Get the active appointment for drag overlay
  const activeAppointment = activeId ? appointments.find(apt => apt.id.toString() === activeId.toString()) : null;

  return (
    <div className="flex flex-col">
      {/* Header with date navigation and selectors */}
      <div className="p-6 bg-base-100 border-b border-base-300">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
            <FaClinicMedical className="text-primary" />
            Facility Flow View
          </h1>
        </div>
        
        {/* Date Navigation */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={goToPreviousDay}
            className="btn btn-sm btn-outline"
            title="Previous Day"
          >
            ← Previous
          </button>
          
          <div className="flex-1 text-center">
            <h2 className="text-xl font-semibold text-base-content">
              {formatDate(currentDate)}
            </h2>
          </div>
          
          <button
            onClick={goToToday}
            className="btn btn-sm btn-primary"
          >
            Today
          </button>
          
          <button
            onClick={goToNextDay}
            className="btn btn-sm btn-outline"
            title="Next Day"
          >
            Next →
          </button>
          
          <DatePickerComponent
            value={currentDate}
            format="dd/MM/yyyy"
            change={(args: { value?: Date | null }) => {
              if (args.value) {
                setCurrentDate(args.value);
              }
            }}
            cssClass="waitingroom-datepicker"
          />
        </div>

        {/* Doctor Selector */}
        <div className="flex items-center gap-4">
          <label className="font-semibold text-base-content">Doctor:</label>
          <select
            value={selectedProviderId || ''}
            onChange={(e) => setSelectedProviderId(e.target.value)}
            className="select select-bordered flex-1 max-w-md"
          >
            <option value="" disabled>Select a doctor</option>
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {cleanProviderName(provider)}
                {provider.primary_specialization?.name && ` - ${provider.primary_specialization.name}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Kanban Board — no fixed height/overflow-hidden here anymore: the
          board's natural height flows into the page, and the single
          page-level scrollbar (far right of the viewport) handles scrolling
          the whole thing, with each column's header staying sticky. */}
      <div className="flex-1 p-6">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-blue-600">Loading appointments...</div>
          </div>
        ) : !selectedProviderId ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-base-content opacity-70">No doctors are available for waiting room view.</div>
          </div>
        ) : appointments.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-base-content opacity-70">No appointments for this doctor on the selected date.</div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-5 gap-4">
              {columns.map(column => (
                <DroppableColumn key={column.id} column={column}>
                  <SortableContext
                    items={column.appointments.map(apt => apt.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    {column.appointments.map(appointment => (
                      <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        isDragging={activeId === appointment.id}
                      />
                    ))}
                  </SortableContext>
                </DroppableColumn>
              ))}
            </div>
            
            <DragOverlay>
              {activeAppointment ? (
                <AppointmentCard appointment={activeAppointment} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {showNoProvidersModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">No Providers Available</h3>
            <p className="py-4">There are presently no Providers, would you like to add one?</p>
            <div className="modal-action">
              <button className="btn" onClick={() => setShowNoProvidersModal(false)}>
                Not Now
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowNoProvidersModal(false);
                  navigate('/facilities/providers');
                }}
              >
                Add Provider
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowNoProvidersModal(false)}></div>
        </div>
      )}
    </div>
  );
};

export default WaitingRoom;
